<template>
  <v-row>
    <v-col cols="12" class="mb-8">
      <TextBody :text="topicTextInfo" />
      <ForumWysiwygWithUploader
        :value="inputText"
        :viewContext="viewContext"
        @input="handleInput"
        :getMentionables="getMentionableFunc"
        @mouseleave.native="handleMouseLeave"
        id="topic-form__textInput"
        @attachments-valid="updateValidationState"
        :load-attachment="loadAttachment"
      />
      <p
        class="body-2 error--text mt-1"
        v-if="wysiwygErrorMessage && wysiwygErrorMessage.length"
      >
        {{ wysiwygErrorMessage }}
      </p>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { ref, defineComponent, PropType } from "vue"
import Header from "../UI/Header.vue"
import TextBody from "../UI/TextBody.vue"
import { TranslateResult } from "vue-i18n"
import { ViewContext } from "concerns/useContextName"
import useMentionSearch from "concerns/useMentionSearch"
import { FileAndId } from "app/modules/core/types/dtos"
import BasicWysiwyg from "components/Wysiwyg/BasicWysiwyg.vue"
import ForumWysiwygWithUploader from "components/Wysiwyg/ForumWysiwygWithUploader.vue"
import { topicsApi } from "store/api"
import { Attachment } from "suus-api"

export default defineComponent({
  name: "TopicFormPanelDescription",
  components: { ForumWysiwygWithUploader, BasicWysiwyg, Header, TextBody },
  props: {
    inputText: { type: String },
    viewContext: { type: Object as PropType<ViewContext> },
    inputTextValidation: { type: Array, default: () => [] },
    addFile: { type: Function as PropType<(fileAndId: FileAndId) => void> },
    updateValidationState: {
      type: Function as PropType<(isValid: boolean) => void>,
    },
    topicId: { type: Number, required: false },
  },
  setup(props, context) {
    const wysiwygErrorMessage = ref(null as string | null)

    const validationState = ref(true)
    function updateValidationState(isValid: boolean) {
      validationState.value = isValid
      props.updateValidationState(isValid)
    }

    function validate(val) {
      wysiwygErrorMessage.value = ""
      props.inputTextValidation.forEach((rule: any) => {
        const message = rule(val)
        if (typeof message === "string") {
          wysiwygErrorMessage.value += `${message} `
        }
      })

      if (!validationState.value) {
        wysiwygErrorMessage.value +=
          "Warte bis alle Anhänge hochgeladen sind oder entferne fehlerhafte Anhänge. "
      }
    }

    function handleMouseLeave() {
      validate(props.inputText)
    }
    function handleInput(val) {
      context.emit("update-field", { key: "inputText", val })
      validate(val)
    }

    const { mentionableFunction: getMentionableFunc } = useMentionSearch(
      props.viewContext
    )
    const attachments = ref<Attachment[]>([])

    const loadAttachments = async (): Promise<Attachment[]> => {
      // if new topic is created, return empty array
      if (!props.topicId) {
        return []
      }

      const { data } = await topicsApi.getTopicAttachments(
        props.topicId.toString()
      )
      return data
    }

    // Delay loading until there is the first request for it & do it only once
    const getAttachments = async () => {
      if (attachments.value.length === 0) {
        attachments.value = await loadAttachments()
        console.log("Loading attachments the first time", attachments.value)
      }
      console.log("Returning already loaded attachments", attachments.value)
      return attachments.value
    }

    const loadAttachment = async (frontendId: string): Promise<Attachment> => {
      const attachments = await getAttachments()
      const attachment = attachments.find((a) => a.frontend_id === frontendId)
      if (attachment) {
        return attachment
      } else {
        console.error("Attachment not found!", frontendId, attachments)
        throw "Attachment not in list of topic attachments!"
      }
    }

    return {
      handleInput,
      getMentionableFunc,
      handleMouseLeave,
      wysiwygErrorMessage,
      updateValidationState,
      loadAttachment,
    }
  },
  computed: {
    topicTextInfo(): TranslateResult {
      return this.$t("components.topic_form.wysiwyg_info")
    },
  },
})
</script>
